import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// Protected Route Component (eager — usado na lógica de render, é pequeno)
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';

// Pages e componentes carregados sob procura (code-splitting) — saem do bundle
// inicial, o que aligeira o primeiro carregamento (sobretudo em mobile).
const HomePage = lazy(() => import('../pages/Home/HomePage'));
const DesignReview = lazy(() => import('../design/DesignReview'));
const TorneioPublicoScreen = lazy(() => import('../mobile/screens/games/torneio/TorneioPublicoScreen'));
const TorneioTvScreen = lazy(() => import('../mobile/screens/games/torneio/TorneioTvScreen'));
// Torneios no PC: os ecrãs do telemóvel dentro de um wrapper de ecrã largo.
const TorneiosDesktop = lazy(() => import('../desktop/torneios/TorneiosDesktop'));
const TournamentListScreen = lazy(() => import('../mobile/screens/games/torneio/TournamentListScreen'));
const SavedTournamentEditor = lazy(() => import('../mobile/screens/games/torneio/SavedTournamentEditor'));
const About = lazy(() => import('../pages/About/About'));
const LoginPage = lazy(() => import('../pages/LoginPage/LoginPage'));
const Register = lazy(() => import('../pages/SignUp/Register'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage/ResetPasswordPage'));
const SobeDesce6Page = lazy(() => import('../pages/SobeDescePage/SobeDesce6Page/SobeDesce6Page'));
const SobeDesce8Page = lazy(() => import('../pages/SobeDescePage/SobeDesce8Page/SobeDesce8Page'));
const SobeDesce12Page = lazy(() => import('../pages/SobeDescePage/SobeDesce12Page/SobeDesce12Page'));
const SobeDesce16Page = lazy(() => import('../pages/SobeDescePage/SobeDesce16Page/SobeDesce16Page'));
const PadelMatch = lazy(() => import('../pages/PadelMatch/PadelMatch'));
const PlayerComponent = lazy(() => import('../pages/Player/playerComponent'));
const CriadorDeEquipesPage = lazy(() => import('../pages/CriadorDeEquipes_duplas/CriadordeEquipesPage'));
const ClubCalendarPage = lazy(() =>
  import('../pages/ClubCalendarPage/ClubCalendarPage').then(m => ({ default: m.ClubCalendarPage }))
);
const PageClubPage = lazy(() => import('../pages/PageClubPage/PageClubPage'));

const UserProfileWrapper = lazy(() => import('../components/Profile/UserProfileWrapper'));
const CalculadoraApp6 = lazy(() => import('../components/Games/SobeDesce/SobeDesce6/CalculadoraApp6'));
const CalculadoraApp8 = lazy(() => import('../components/Games/SobeDesce/SobeDesce8/CalculadoraApp8'));
const CalculadoraApp12 = lazy(() => import('../components/Games/SobeDesce/SobeDesce12/CalculadoraApp12'));
const CalculadoraApp16 = lazy(() => import('../components/Games/SobeDesce/SobeDesce16/CalculadoraApp16'));
const GameDetail = lazy(() => import('../components/Profile/GameDetail/GameDetail'));
const MyGamesInvites = lazy(() => import('../components/Profile/MyGamesInvites/MyGamesInvites'));
const ReservationsCalendar = lazy(() =>
  import('../components/ReservationsCalendar/ReservationsCalendar').then(m => ({ default: m.ReservationsCalendar }))
);
const CriadorDeEquipes = lazy(() => import('../components/HomeComponents/CriadorDeEquipes/CriadorDeEquipes'));
const TestPage = lazy(() => import('../components/TestPage'));
const EditarClube = lazy(() => import('../components/Profile/MeuClube/EditarClube'));

// Initialize Stripe
const stripePromise = loadStripe(
  'pk_live_51QBhabFhlaAOoYB7lylrbEFOufrcGpoBiTXLeEd0NMkNGi8AHZFRSnxdq4bD2Xzl4c170pUXDAhmnSAWWjqvkDOr00kV1QnbCe'
);

// Group routes by access level
// Rotas públicas do aplicativo (não precisam de autenticação)
const publicRoutes = [
  { path: '/', element: <HomePage /> },
  { path: '/about', element: <About /> },
  { path: '/sobre', element: <About /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <Register /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  ...(import.meta.env.DEV ? [{ path: '/design', element: <DesignReview /> }] : []),
  { path: '/reset-password', element: <ResetPasswordPage /> }, // token vem em ?token=
  // A página do clube é o link que o clube partilha: tem de abrir a quem ainda
  // não tem conta. Estava nas rotas de jogos, que são todas protegidas, e no PC
  // quem clicava no link levava com o ecrã de login.
  { path: '/club/:clubSlug/page', element: <PageClubPage /> },
  // Resultados públicos de um torneio: o mesmo ecrã da app, num link só.
  // No PC, os resultados públicos com o mesmo aspeto dos torneios (fundo, cabeçalho).
  { path: '/t/:codigo', element: <TorneiosDesktop titulo="Resultados"><TorneioPublicoScreen /></TorneiosDesktop> },
  // Modo ecrã: a TV do clube. Público, ecrã inteiro, atualiza sozinho.
  { path: '/t/:codigo/tv', element: <TorneioTvScreen /> },
];

// Rotas que precisam de autenticação (perfil, jogos pessoais, etc.)
const protectedRoutes = [
  { path: '/test-apis', element: <ProtectedRoute element={<TestPage />} /> },
  { path: '/profile', element: <ProtectedRoute element={<UserProfileWrapper />} /> },
  { path: '/profile/account-change-password', element: <ProtectedRoute element={<UserProfileWrapper />} /> },
  { path: '/profile/my-games', element: <ProtectedRoute element={<MyGamesInvites />} /> },
  { path: '/profile/my-games/:gameId', element: <ProtectedRoute element={<GameDetail />} /> },
  { path: '/club/:clubSlug/edit', element: <ProtectedRoute element={<EditarClube />} /> },
];

// Rotas de jogos e funcionalidades (agora exigem autenticação)
const gameRoutes = [
  { path: '/sobe-desce6', element: <SobeDesce6Page /> },
  { path: '/sobe-desce8', element: <SobeDesce8Page /> },
  { path: '/6-jogadores', element: <CalculadoraApp6 /> },
  { path: '/8-jogadores', element: <CalculadoraApp8 /> },
  { path: '/player-component', element: <PlayerComponent /> },
  { path: '/calculadora', element: <CalculadoraApp6 /> },
  { path: '/club/:clubSlug/reservations/', element: <ReservationsCalendar /> },
  { path: '/club/:clubSlug/reservations/:startDate', element: <ClubCalendarPage /> },
  { path: '/criador-de-equipes', element: <CriadorDeEquipes /> },
  { path: '/criador-de-equipespage', element: <CriadorDeEquipesPage /> },
];

// Rotas premium (agora exigem autenticação; assinatura pode ser adicionada no futuro)
const premiumRoutes = [
  { path: '/sobe-desce12', element: <SobeDesce12Page /> },
  { path: '/sobe-desce16', element: <SobeDesce16Page /> },
  { path: '/12-jogadores', element: <CalculadoraApp12 /> },
  { path: '/16-jogadores', element: <CalculadoraApp16 /> },
  { path: '/PadelMatch', element: <PadelMatch /> },
  { path: '/torneios/:tipo', element: <TorneiosDesktop><TournamentListScreen /></TorneiosDesktop> },
  { path: '/torneios/:tipo/:savedId', element: <TorneiosDesktop><SavedTournamentEditor /></TorneiosDesktop> },
  // Caminhos antigos do PC e do hub do telemóvel: levam aos torneios novos.
  { path: '/torneios', element: <Navigate to="/torneios/torneio" replace /> },
  { path: '/troneios', element: <Navigate to="/torneios/torneio" replace /> },
  { path: '/MiniTroneio_8Duplas_5Jogos', element: <Navigate to="/torneios/mini8" replace /> },
  { path: '/TodosContraTodos6Dupla', element: <Navigate to="/torneios/liga" replace /> },
  { path: '/torneio-duplas', element: <Navigate to="/torneios/duplas" replace /> },
  { path: '/jogos', element: <Navigate to="/" replace /> },
];

// Fallback simples enquanto o chunk da rota carrega.
const RouteFallback: React.FC = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#2e7d32',
      fontFamily: 'Manrope, system-ui, sans-serif',
      fontWeight: 600,
    }}
  >
    A carregar…
  </div>
);

// Rotas organizadas com sistema de autenticação melhorado
const AppRoutes: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* 🌐 Rotas Públicas - Acesso livre */}
          {publicRoutes.map(route => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

          {/* 🔐 Rotas Protegidas - Precisam de autenticação */}
          {protectedRoutes.map(route => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

          {/* 🎮 Rotas de Jogos - Precisam de autenticação */}
          {gameRoutes.map(route => (
            <Route key={route.path} path={route.path} element={<ProtectedRoute element={route.element} />} />
          ))}

          {/* 🥇 Rotas Premium - Precisam de autenticação */}
          {premiumRoutes.map(route => (
            <Route key={route.path} path={route.path} element={<ProtectedRoute element={route.element} />} />
          ))}
        </Routes>
      </Suspense>
    </Elements>
  );
};

export default AppRoutes;
